/**
 * Connection that wraps a socket and provides an interface to interact
 */
import type { Auth } from "./auth";

export type ConnectionOptions = {
  auth?: Auth;
};

export type ConnectionEventListener = (
  conn: Connection,
  eventData?: any
) => void;

type Events = "ready" | "disconnected" | "reconnect-error";

export class Connection {
  options: ConnectionOptions;

  eventListeners: Map<string, ConnectionEventListener[]>;

  suspendReconnectPromise?: Promise<void>;

  constructor(options: ConnectionOptions) {
    // connection options
    this.options = options;
    // map of event listeners
    this.eventListeners = new Map();

    window.addEventListener("online", (event) =>
      this.handleConnectionChange(event)
    );
    window.addEventListener("offline", (event) =>
      this.handleConnectionChange(event)
    );
  }

  private handleConnectionChange(event) {
    if (event.type === "offline") {
      this.fireEvent("disconnected");
    }
    if (event.type === "online") {
      this.fireEvent("ready");
    }
  }

  get connected() {
    // Using conn.socket.OPEN instead of WebSocket for better node support
    return true;
  }

  addEventListener(eventType: Events, callback: ConnectionEventListener) {
    let listeners = this.eventListeners.get(eventType);

    if (!listeners) {
      listeners = [];
      this.eventListeners.set(eventType, listeners);
    }

    listeners.push(callback);
  }

  removeEventListener(eventType: Events, callback: ConnectionEventListener) {
    const listeners = this.eventListeners.get(eventType);

    if (!listeners) {
      return;
    }

    const index = listeners.indexOf(callback);

    if (index !== -1) {
      listeners.splice(index, 1);
    }
  }

  fireEvent(eventType: Events, eventData?: any) {
    (this.eventListeners.get(eventType) || []).forEach((callback) =>
      callback(this, eventData)
    );
  }

  suspendReconnectUntil(suspendPromise: Promise<void>) {
    this.suspendReconnectPromise = suspendPromise;
  }

  suspend() {
    if (!this.suspendReconnectPromise) {
      throw new Error("Suspend promise not set");
    }
    // this.socket.close();
  }
}
